// Inline-Skill-Befehle: erkennt "/name" mitten im Text und normalisiert die
// Nachricht zu "/name <rest>", damit der bestehende Slash-Pipeline greift.
// Reine Funktion — kein IO, kein React.
//
// Nicht ausloesen bei:
// - "und/oder" (das "/oder" ist kein Befehl)
// - "_Research/foo" oder "[[_Research/x]]" (kein Whitespace/Anfang vor dem Slash)
// - URLs wie "https://a.b/presentation" (Slash nicht an Token-Grenze)
// - "/presentationx" (Name muss exakt matchen, inkl. Wortgrenze dahinter)
// - unbekannten Namen

/**
 * Sucht das erste Token der Form `/name`, das einem bekannten Skill-Namen
 * exakt entspricht (case-insensitive), und baut die Nachricht so um, dass der
 * Befehl an den Anfang rückt: `/name <text davor> <text danach>`.
 *
 * Beginnt `text` bereits mit `/`, wird er unveraendert zurueckgegeben.
 * Kein Treffer -> unveraendert.
 */
export function normalizeInlineCommand(
  text: string,
  knownNames: ReadonlySet<string>,
): string {
  if (text.startsWith('/')) return text

  // Kanonische Namen (Originalschreibweise) fuer die case-insensitive Suche.
  const canonicalByLower = new Map<string, string>()
  for (const name of knownNames) {
    canonicalByLower.set(name.toLowerCase(), name)
  }
  if (canonicalByLower.size === 0) return text

  // /(^|\s)\/(name)(?=\s|$) — Slash an Textanfang oder nach Whitespace,
  // Name exakt, gefolgt von Whitespace oder Textende. Der Name selbst darf
  // keine Nicht-Wortzeichen enthalten (Skills sind [a-z0-9-]).
  const pattern = /(^|\s)\/([a-z0-9-]+)(?=\s|$)/gi
  let match: RegExpExecArray | null
  while ((match = pattern.exec(text)) !== null) {
    const canonical = canonicalByLower.get((match[2] ?? '').toLowerCase())
    if (!canonical) continue

    const before = text.slice(0, match.index).trim()
    // match[1] ist das fuehrende Whitespace-Zeichen (oder '' am Anfang).
    const after = text.slice(match.index + match[0].length).trim()
    const rest = [before, after].filter((part) => part.length > 0).join(' ')
    return `/${canonical} ${rest}`.trim()
  }

  return text
}
